import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationViewed,
  type Notification,
  type NotificationListResult,
  type NotificationStatusFilter,
  type NotificationTypeFilter,
} from '@/api/adapters/notifications';

export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;

/**
 * Notification inbox query (PRD 08). `type` + `status` are sent to the server
 * (server-side filter). `unreadCount` is the total unread across all
 * notifications regardless of the active filters, so it can drive the GNB bell
 * badge consistently.
 */
export function useNotifications(
  type: NotificationTypeFilter = 'ALL',
  status: NotificationStatusFilter = 'ALL',
) {
  const query = useQuery<NotificationListResult>({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, type, status],
    queryFn: () => fetchNotifications(type, status),
    staleTime: 60_000,
  });

  const notifications: Notification[] = query.data?.items ?? [];
  const unreadCount = query.data?.unreadCount ?? 0;

  return {
    notifications,
    unreadCount,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/** Mark a single notice read; invalidates every notifications query (all filters + GNB). */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });
}

/**
 * SHCT-652 — count one 조회 of a SYSTEM 공지 (어드민 PRD 13 §3.1: every open increments).
 *
 * No `onSuccess` invalidation: 조회수 is rendered only in Admin Web, so nothing in the medical
 * notification cache depends on it. Errors are swallowed at the call site — a failed counter must
 * never block the staff from reading the notice.
 */
export function useMarkNotificationViewed() {
  return useMutation({
    mutationFn: (id: string) => markNotificationViewed(id),
  });
}

/** Mark all notices read; invalidates every notifications query. */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });
}
